import { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import styles from "./ReactChart.module.css";

const CustomLegend = ({ payload = [] }) => {
  if (!Array.isArray(payload) || payload.length === 0) return null;

  return (
    <div className={styles.legend} role="list">
      {payload.map((entry) => (
        <div key={entry.dataKey || entry.value} className={styles.legendItem} role="listitem">
          <span className={styles.legendSwatch} style={{ backgroundColor: entry.color }} />
          <span className={styles.legendLabel} style={{ color: entry.color }}>
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const ReactChart = ({
  height = 260,
  type = "line",
  data = [],
  series = [{ key: "y", color: "#4f46e5", name: "value" }],
  xKey = "x",
}) => {
  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const safeSeries = useMemo(
    () => (Array.isArray(series) && series.length ? series : [{ key: "y", color: "#4f46e5" }]),
    [series]
  );

  const normalizedType = String(type || "line").toLowerCase();

  return (
    <div className={styles.wrap} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {normalizedType === "bars" || normalizedType === "bar" ? (
          <BarChart data={safeData} margin={{ top: 8, right: 10, left: 0, bottom: 12 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} />
            <YAxis />
            <Tooltip />
            {safeSeries.length > 1 ? (
              <Legend
                verticalAlign="bottom"
                align="center"
                iconSize={10}
                content={<CustomLegend />}
              />
            ) : null}
            {safeSeries.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                fill={s.color || "#4f46e5"}
                radius={[10, 10, 0, 0]}
                name={s.name || s.key}
              />
            ))}
          </BarChart>
        ) : (
          <LineChart data={safeData} margin={{ top: 8, right: 10, left: 0, bottom: 12 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} />
            <YAxis />
            <Tooltip />
            {safeSeries.length > 1 ? (
              <Legend
                verticalAlign="bottom"
                align="center"
                iconSize={10}
                content={<CustomLegend />}
              />
            ) : null}
            {safeSeries.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stroke={s.color || "#4f46e5"}
                strokeWidth={3}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                name={s.name || s.key}
              />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default ReactChart;
